import { Injectable, Pipe, PipeTransform } from '@angular/core';
import {
  AbstractControl,
  ValidationErrors,
  FormGroup,
  FormArray,
  ValidatorFn,
} from '@angular/forms';
import moment from 'moment';

// Pipe obtener el mensaje de error de un campo una sola vez por cambio del input
@Pipe({ name: 'validationErrorMessage', pure: true, standalone: true })
export class ValidationErrorMessagePipe implements PipeTransform {
  constructor(private validacionService: ValidacionForm) {}

  transform = (errores: ValidationErrors): { message: string } => {
    const errorMessage = this.validacionService.getErrorMessage(errores);
    return { message: errorMessage || 'Error desconocido' };
  };

  // transform = (errores: ValidationErrors): { message: string } | null => {
  //   if (errores) {
  //     const errorMessage = this.validacionService.getErrorMessage(errores);
  //     return errorMessage ? { message: errorMessage } : null;
  //   }
  //   return null;
  // };
}

@Injectable({
  providedIn: 'root',
})
export class ValidacionForm {
  // Método existente para obtener el estado de validación
  getFieldValidationState(
    control: AbstractControl,
  ): 'valid' | 'invalid' | undefined {
    if (!control || control.disabled) return 'valid';
    if (control.value && control.valid) return 'valid';
    if (control.touched && control.dirty) {
      return control.valid ? 'valid' : 'invalid';
    }
    return 'valid';
  }

  // Método existente para obtener errores de validación
  getFieldValidationError(
    control: AbstractControl,
  ): { message: string } | null {
    if (
      (control.touched && control.dirty) ||
      (control.value && control.invalid)
    ) {
      if (control.errors) {
        const errorMessage = this.getErrorMessage(control.errors);
        return errorMessage ? { message: errorMessage } : null;
      }
    }
    return null;
  }

  // Nueva función: Get validation state for form group errors
  getFormGroupValidationState(
    formGroup: FormGroup,
  ): 'valid' | 'invalid' | undefined {
    if (!formGroup) return 'valid';

    // Si el formulario tiene errores y ha sido tocado o modificado
    if (formGroup.errors && (formGroup.touched || formGroup.dirty)) {
      return 'invalid';
    }

    // Si el formulario es válido y ha sido tocado o modificado
    if (formGroup.valid && (formGroup.touched || formGroup.dirty)) {
      return 'valid';
    }

    return 'valid';
  }

  // Nueva función: Get validation error message for form group
  getFormGroupValidationError(
    formGroup: FormGroup,
  ): { message: string } | null {
    if (
      !formGroup ||
      !formGroup.errors ||
      (!formGroup.touched && !formGroup.dirty)
    ) {
      return null;
    }

    // Obtener la primera clave de error
    const firstErrorKey = Object.keys(formGroup.errors)[0];
    const errorValue = formGroup.errors[firstErrorKey];

    // Si el valor del error es una cadena, usarlo directamente
    if (typeof errorValue === 'string') {
      return { message: errorValue };
    }

    // Usar el mensaje predeterminado basado en el tipo de error
    return { message: this.getDefaultErrorMessage(firstErrorKey) };
  }

  // Nuevo método auxiliar para mensajes de error predeterminados
  private getDefaultErrorMessage(errorKey: string): string {
    switch (errorKey) {
      case 'overlappingDates':
        return 'Las fechas se superponen con otra línea de horario';
      case 'invalidDateRange':
        return 'La fecha fin no puede ser anterior a la fecha de inicio';
      case 'dateRange':
        return 'La fecha fin debe ser posterior a la fecha inicio';
      case 'horarioContratoSuperpuestos':
        return 'Las fechas se superponen con las del contrato';
      default:
        return 'Error de validación';
    }
  }

  // Nuevos métodos para FormArray
  getFormArrayItemValidationState(
    formArray: FormArray,
    index: number,
  ): 'valid' | 'invalid' | undefined {
    if (!formArray || index < 0 || index >= formArray.length) return 'valid';

    const item = formArray.at(index);
    return this.getFormGroupValidationState(item as FormGroup);
  }

  getFormArrayItemValidationError(
    formArray: FormArray,
    index: number,
  ): { message: string } | null {
    if (!formArray || index < 0 || index >= formArray.length) return null;

    const item = formArray.at(index);
    return this.getFormGroupValidationError(item as FormGroup);
  }

  // Mensajes de error actualizados incluyendo el nuevo error de solo números
  public getErrorMessage(errors: ValidationErrors): string | null {
    if (errors) {
      if (errors['required']) return 'Este campo es obligatorio';
      if (errors['email']) return 'El correo electrónico no es válido';
      if (errors['pattern']) return 'El formato no es válido';
      if (errors['min']) return `El valor mínimo es ${errors['min'].min}`;
      if (errors['max']) return `El valor máximo es ${errors['max'].max}`;
      if (errors['minlength'])
        return `Mínimo ${errors['minlength'].requiredLength} caracteres`;
      if (errors['maxlength'])
        return `Máximo ${errors['maxlength'].requiredLength} caracteres`;
      if (errors['dateRange'])
        return 'La fecha fin debe ser posterior a la fecha inicio';
      if (errors['invalidNIF']) return 'NIF/NIE no válido';
      if (errors['invalidPhone']) return 'Número de teléfono no válido';
      if (errors['invalidCP']) return 'C.P. no válido';
      if (errors['soloNumeros']) return 'Este campo solo acepta números';
      if (errors['contraseña']) return 'Contraseña incorrecta';
      if (errors['usuarioNoEncontrado']) return 'Usuario no encontrado';
      if (errors['credencialesInvalidas'])
        return 'Correo o contraseña incorrectos';
      if (errors['codigoIncorrecto'])
        return 'Código de acceso inválido';
      if (errors['invalidEmail']) return 'Formato invalido';
      if (errors['overlappingDates'])
        return 'Las fechas se superponen con otra línea de horario';
      if (errors['invalidDateRange'])
        return 'La fecha fin no puede ser anterior a la fecha de inicio';
      if (errors['emailExists']) return 'El email ya está registrado';
      if (errors['usernameExists'])
        return 'El nombre de usuario ya está registrado';
      if (errors['nifExists']) return 'El NIF ya está registrado';
      if (errors['nafExists']) return 'El NAF ya está registrado';
      if (errors['categoriaExistente']) return 'La categoría ya existe';
      if (errors['invalidNumeroTarjeta']) return 'Número de tarjeta no válido';
      if (errors['invalidCvv']) return 'CVV no válido';
      if (errors['accesoDenegado']) return 'No tienes permisos de acceso';
    }
    return null;
  }

  // Validador para campos que solo aceptan números
  soloNumerosValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const valid = /^\d+$/.test(control.value.toString());
      return valid ? null : { soloNumeros: true };
    };
  }

  // Validador para teléfonos españoles (fijos y móviles)
  telefonoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const telefono = control.value.toString();
      const soloNumerosRegex = /^\d+$/;

      return soloNumerosRegex.test(telefono) ? null : { invalidPhone: true };
    };
  }

  // Validador para código postal español
  cpValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null) return null;
      const cp = control.value.toString();
      // Codigo postal de 5 digitos
      const cpRegex = /^(0[1-9]|[1-4][0-9]|5[0-2])[0-9]{3}$/;
      return cpRegex.test(cp) ? null : { invalidCP: true };
    };
  }

  // Validador para NAF (Número de Afiliación a la Seguridad Social)
  nafValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const naf = control.value.toString();
      // El NAF debe tener 12 dígitos
      const nafRegex = /^\d{12}$/;
      return nafRegex.test(naf) ? null : { pattern: true };
    };
  }

  nifValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const nif = control.value.toString().toUpperCase();

      // Expresión regular para validar formato NIF/NIE
      const nifRegex = /^[XYZ\d][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/;

      if (!nifRegex.test(nif)) {
        return { invalidNIF: true };
      }

      // Letras válidas para NIF
      const validLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';

      // Obtener número y letra
      let number = nif.slice(0, -1);
      const letter = nif.slice(-1);

      // Reemplazar letra inicial del NIE por su número correspondiente
      if (isNaN(parseInt(number[0]))) {
        const nieFirst = number[0];
        switch (nieFirst) {
          case 'X':
            number = '0' + number.slice(1);
            break;
          case 'Y':
            number = '1' + number.slice(1);
            break;
          case 'Z':
            number = '2' + number.slice(1);
            break;
        }
      }

      // Calcular letra correcta
      const index = parseInt(number) % 23;
      const correctLetter = validLetters[index];

      // Verificar si la letra es correcta
      return letter === correctLetter ? null : { invalidNIF: true };
    };
  }

  // Método existente para marcar formulario como tocado
  markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      } else {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }

  // Método existente para resetear estado del formulario
  resetFormState(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.resetFormState(control);
      } else {
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
  }

  numeroTarjetaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const numeroTarjetaRegex = /^\d{13,19}$/;

      return numeroTarjetaRegex.test(control.value)
        ? null
        : { invalidNumeroTarjeta: true };
    };
  }

  cvvValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const cvvRegex = /^\d{3,4}$/;

      return cvvRegex.test(control.value) ? null : { invalidCvv: true };
    };
  }
}
